import { Endpoints } from '@/remote/endpoints'
import { Button } from '@/view/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/view/components/ui/dropdown-menu'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/view/components/ui/table'
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { Eye, Image, MoreHorizontal, Trash2, UserPen } from 'lucide-react'
import * as React from 'react'
import { DialogDelete } from '../modals/modal-delete'
import { DialogDetailEmployee } from '../modals/modal-detail-employee'
import { DialogEditEmployee } from '../modals/modal-edit-employee'
import { convertDateDisplayFormat } from '../utils/formatDateDisplay'
import { EmployeeData } from '../validators/employee-form-validator'
import { ScrollArea } from './ui/scroll-area'

interface Props {
  data: EmployeeData[]
  getRowsSelecteds?: (data: any) => void
  getRefreshList?: () => void
}

export function DataTableEmployee({ data, getRefreshList }: Props) {
  const columns: ColumnDef<EmployeeData>[] = [
    {
      id: 'select',
      header: '',
      cell: () => <Image />,
      enableSorting: false,
      enableHiding: false
    },
    {
      accessorKey: 'nome',
      header: 'Nome',
      cell: ({ row }) => <div className="capitalize">{row.getValue('nome')}</div>
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => {
        const { contato } = row.original
        return <div className="lowercase"> {contato?.email}</div>
      }
    },
    {
      accessorKey: 'cpf',
      header: 'Cpf',
      cell: ({ row }) => {
        const formatCpf = (cpf: string) => {
          return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
        }

        const formattedCpf = formatCpf(row.getValue('cpf'))

        return <div className="lowercase">{formattedCpf}</div>
      }
    },
    {
      accessorKey: 'dataNascimento',
      header: 'Data de Nascimento',
      cell: ({ row }) => {
        const formatBirthDate = (birthDate: string) => {
          if (!birthDate) return ''
          return convertDateDisplayFormat(birthDate.split('T')[0]) ?? ''
        }

        const formattedBirthDate = formatBirthDate(row.getValue('dataNascimento'))

        return <div className="lowercase">{formattedBirthDate}</div>
      }
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const employee = row.original
        const [dialogDelete, setDialogDelete] = React.useState(false)

        const [dialogDetail, setDialogDetail] = React.useState(false)

        const [dialogEdit, setDialogEdit] = React.useState(false)

        const closeModalDelete = () => setDialogDelete(false)

        const closeModelDetail = () => setDialogDetail(false)

        const closeModelEdit = () => setDialogEdit(false)

        return (
          <DropdownMenu>
            <DialogDelete
              isOpen={dialogDelete}
              closeDialog={closeModalDelete}
              id={employee?.id || 0}
              url={Endpoints.employee}
              label="Excluir Funcionário"
              name={employee?.nome}
              updateList={getRefreshList}
            />
            <DialogDetailEmployee isOpen={dialogDetail} closeDialog={closeModelDetail} id={employee?.id || 0} />
            <DialogEditEmployee isOpen={dialogEdit} closeDialog={closeModelEdit} id={employee.id || 0} updateList={getRefreshList} />
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir Menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              <DropdownMenuLabel>Ações</DropdownMenuLabel>
              <DropdownMenuItem className="flex gap-2 cursor-pointer" onClick={() => setDialogDetail(true)}>
                <Eye height={24} width={30} className="text-blue-500 cursor-pointer p-1  hover:text-blue-400" />
                Detalhes
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="flex gap-2 cursor-pointer" onClick={() => setDialogEdit(true)}>
                <UserPen height={24} width={30} className="text-blue-500 cursor-pointer p-1  hover:text-blue-400" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="flex gap-2 cursor-pointer" onClick={() => setDialogDelete(true)}>
                <Trash2 height={24} width={30} className="text-red-500 cursor-pointer p-1  hover:text-red-400" />
                Apagar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      }
    }
  ]

  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = React.useState({})

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    initialState: { pagination: { pageSize: 100 } },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection
    }
  })

  return (
    <div className="w-full">
      <div className="rounded-md border min-w-full">
        <ScrollArea className="m-4 h-container-members-list relative">
          <Table className="border-none">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return <TableHead key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</TableHead>
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center"></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
    </div>
  )
}
